/**
 * Typed client for `/board/meetings` endpoints — read-only for
 * shareholders. Non-board shareholders only see meetings that have
 * been concluded + published (server-side filter on the same
 * controller the publisher dashboard's shareholder portal called).
 *
 * Endpoint contract verified against
 * `nurev-api/app/controllers/board.js`. Shareholder JWT (role
 * 'shareholder' or 'admin') is required.
 */
import { client } from './client';

export type MeetingType = 'annual' | 'special' | 'board' | string;
export type ResultOutcome =
  | 'passed'
  | 'failed'
  | 'no_quorum'
  | 'pending'
  | string;
export type ItemType = 'binary' | 'multi' | string;

export interface BoardMeeting {
  _id: string;
  name: string;
  meetingType: MeetingType;
  scheduledAt?: string;
  concludedAt?: string;
  recordingUrl?: string | null;
  minutes?: string | null;
  resultsSummary?: MeetingResultsSummary | null;
}

export interface MeetingResultsSummary {
  quorumReached: boolean;
  attendance: {
    present: number;
    proxy?: number;
    absent?: number;
  };
  itemResults?: MeetingItemResult[];
}

export interface MeetingItemResult {
  itemId?: string;
  title: string;
  itemType: ItemType;
  outcome: ResultOutcome;
  sharesFor?: number;
  sharesAgainst?: number;
  votesAbstain?: number;
  winner?: string;
}

export interface AttendanceRow {
  shareholderId: string;
  name: string;
  status: 'present' | 'proxy' | 'absent' | string;
}

export interface MeetingDetailResponse {
  meeting: BoardMeeting;
  items?: MeetingItemResult[];
  attendance?: AttendanceRow[];
}

export interface MeetingsListResponse {
  meetings: BoardMeeting[];
}

const get = async <T>(path: string): Promise<T> => {
  const r = await client.get<{ success?: boolean; data?: T } | T>(path);
  // Same unwrap as portal.ts — the controller responds with
  // `{success, data}` via middleware/utils.handleSuccess.
  const body = r.data as { success?: boolean; data?: T };
  if (
    body &&
    typeof body === 'object' &&
    'data' in body &&
    body.data !== undefined
  ) {
    return body.data;
  }
  return r.data as T;
};

export const fetchBoardMeetings = () =>
  get<MeetingsListResponse>('/board/meetings');

export const fetchBoardMeeting = (id: string) =>
  get<MeetingDetailResponse>(`/board/meetings/${encodeURIComponent(id)}`);
